/**
 * MessagingLayout — Fullscreen wrapper for messaging routes.
 *
 * Desktop: Renders the Stackflow-based MessagingStack for split-view navigation.
 * Mobile:  Renders page components directly from URL params, bypassing Stackflow.
 *          This avoids history conflicts between Stackflow's historySyncPlugin
 *          and react-router (duplicate entries, ghost states, blank screens).
 *          SpatialOverlay (in AppLayout) owns all push/pop animation on mobile.
 */

import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessagingStack } from '@/stackflow';
import { AnimatedGradientBackground } from '@/components/glass';
import { ChatPage, NewConversationPage, StarredMessagesPage } from '@/features/messaging/pages';
import ChannelInfoPage from '@/features/messaging/pages/ChannelInfoPage';
import NewChannelPage from '@/features/messaging/pages/NewChannelPage';
import { ThreadPanel } from '@/features/messaging/components/ThreadPanel';
import { useMessages } from '@/features/messaging/hooks';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';

/** Mobile thread wrapper — replicates ThreadActivity without Stackflow dependency. */
function MobileThreadView({ conversationId, messageId }: { conversationId: string; messageId: string }) {
  const navigate = useNavigate();
  const { data: messagesData } = useMessages(conversationId);

  const rootMessage = useMemo(() => {
    if (!messagesData?.pages) return null;
    for (const page of messagesData.pages) {
      const msg = page.messages.find(m => m.id === messageId);
      if (msg) return msg;
    }
    return null;
  }, [messagesData, messageId]);

  if (!rootMessage) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border border-white/20 border-t-slate-800" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-xl">
      <ThreadPanel
        conversationId={conversationId}
        rootMessage={rootMessage}
        onClose={() => navigate(`/messages/${conversationId}`, { replace: true })}
      />
    </div>
  );
}

/** Mobile: route-based rendering without Stackflow. */
function MobileMessagingRouter() {
  const { pathname, search } = useLocation();
  const searchParams = new URLSearchParams(search);

  // /messages/:conversationId/thread/:messageId
  const threadMatch = pathname.match(/^\/messages\/([^/]+)\/thread\/([^/]+)/);
  if (threadMatch) {
    return <MobileThreadView conversationId={threadMatch[1]} messageId={threadMatch[2]} />;
  }

  // /messages/:conversationId/info
  const infoMatch = pathname.match(/^\/messages\/([^/]+)\/info/);
  if (infoMatch) {
    return <ChannelInfoPage conversationId={infoMatch[1]} />;
  }

  // /messages/new
  if (pathname === '/messages/new') {
    return <NewConversationPage />;
  }

  // /messages/new-channel
  if (pathname === '/messages/new-channel') {
    return <NewChannelPage />;
  }

  // /messages/starred
  if (pathname === '/messages/starred') {
    return <StarredMessagesPage />;
  }

  // /messages/:conversationId
  const chatMatch = pathname.match(/^\/messages\/([^/]+)/);
  if (chatMatch) {
    return (
      <ChatPage
        conversationId={chatMatch[1]}
        scrollToMessageId={searchParams.get('scrollTo') ?? undefined}
      />
    );
  }

  return null;
}

export function MessagingLayout() {
  const isMobile = useMediaQuery(MD_BREAKPOINT);

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden">
      <AnimatedGradientBackground />
      {isMobile ? <MobileMessagingRouter /> : <MessagingStack />}
    </div>
  );
}
